import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./tab.module.scss";
import Badge from "../tag/Badge";

type TTabType = "underline" | "r50" | "r8" | "text" | "image" | "toggle"; // 탭 타입 정의
type TTabUnderlineDetailType = "fixed" | "scrollable"; // 탭 언더라인 디테일 타입 정의
type TR50DetailType = "default" | "brown" | "dropdown"; // r50 디테일 타입 정의
type TR8DetailType = "default" | "dropdown"; // r8 디테일 타입 정의
type TTextDetailType = "font-22" | "font-15"; // text 디테일 타입 정의

type TTabTypeResult<T> = T extends "underline"
  ? TTabUnderlineDetailType
  : T extends "r50"
    ? TR50DetailType
    : T extends "r8"
      ? TR8DetailType
      : T extends "text"
        ? TTextDetailType
        : "";

interface TabProps {
  className?: string;
  activeTabIndex?: number;
  tabs: {
    label: string;
    count?: number;
    component?: React.ReactNode;
    url?: string; // 이미지 타입일 때 이미지 경로
    badge?: {
      type: "text" | "dot";
      text?: string;
      isShow?: boolean;
      backgroundColor?: string;
      selectedbackgroundColor?: string;
    }[];
    onClick?: () => void;
  }[];
  type?: TTabType;
  detailType?: TTabTypeResult<TTabType>;
  onSelect?: (index: number) => void;
  scrollToSelected?: boolean;
  scrollToSelectedTabContent?: boolean;
  tabContent?: HTMLElement | null;
  scrollToSelectedTabContentOffsetTop?: number;
  scrollToSelectedTabContentOffsetBottom?: number;
  scrollToSelectedTabParent?: HTMLElement | null;
  countColor?: string;
  marginForCustom?: string;
}

const Tab: React.FC<TabProps> = ({
  className,
  activeTabIndex,
  tabs,
  type = "underline",
  detailType,
  onSelect,
  scrollToSelected = false,
  scrollToSelectedTabContent,
  tabContent,
  scrollToSelectedTabContentOffsetTop = 0,
  scrollToSelectedTabContentOffsetBottom = 0,
  scrollToSelectedTabParent,
  countColor,
  marginForCustom,
}) => {
  // 탭 참조
  const tabRef = useRef<HTMLDivElement | null>(null);

  // 현재 포커스된 탭 인덱스
  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  // 클릭된 탭 인덱스 (탭 클릭 시에만 사용)
  const clickIndex = useRef<null | number>(null);

  // 탭이 활성화 되었을 때 처리 (부모에서 activeTabIndex를 변경할 때도 있어서 아래와 같이 처리)
  useEffect(() => {
    if (
      activeTabIndex !== undefined &&
      activeTabIndex !== null &&
      activeTabIndex !== focusedIndex
    ) {
      setFocusedIndex(activeTabIndex);
    }
  }, [activeTabIndex, focusedIndex]);

  // 탭 클릭 시 처리
  const handleTabClick = useCallback(
    (index: number) => {
      clickIndex.current = index;
      if (activeTabIndex === undefined) {
        setFocusedIndex(index);
      }
      if (onSelect) onSelect(index);
      if (typeof window === "undefined") return;

      // 탭 자체 스크롤
      if (scrollToSelected && tabRef?.current) {
        // 활성화된 탭으로 스크롤
        if (!tabRef.current) return;
        const tab = type === "toggle" ? tabRef?.current : tabRef?.current?.querySelector("ul");
        if (tab) {
          const selectedTab = tab.querySelectorAll("li")[index];
          if (!selectedTab) return;
          const containerLeft = tab.getBoundingClientRect().left;
          const tabLeft = selectedTab.getBoundingClientRect().left;
          const offset = tabLeft - containerLeft;

          // 스타일에서 패딩 값을 가져오기
          const computedStyle =
            type === "toggle"
              ? window.getComputedStyle(tab.querySelector("ul") as HTMLElement)
              : window.getComputedStyle(tab);
          const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
          tab.scrollTo({
            left: Math.max(tab.scrollLeft + offset - paddingLeft, 0),
            behavior: "smooth",
          });
        }
      }

      // 해당하는 탭 컨텐츠로 스크롤
      if (scrollToSelectedTabContent && tabContent) {
        const selectedTabContent = tabContent?.children[index] as HTMLElement;
        if (selectedTabContent) {
          let scrollTarget = typeof window !== "undefined" ? window : scrollToSelectedTabParent;
          let finalScrollTop = 0;

          if (!scrollToSelectedTabParent && typeof window !== "undefined") {
            finalScrollTop = Math.min(
              document.documentElement.scrollHeight - window.innerHeight,
              selectedTabContent.offsetTop - scrollToSelectedTabContentOffsetTop,
            );
            if (Math.abs(window.scrollY - finalScrollTop) <= 1) {
              clickIndex.current = null;
            }
          } else if (scrollToSelectedTabParent) {
            finalScrollTop = Math.min(
              scrollToSelectedTabParent.scrollHeight - scrollToSelectedTabParent.clientHeight,
              selectedTabContent.offsetTop - scrollToSelectedTabContentOffsetTop,
            );

            if (Math.abs(scrollToSelectedTabParent.scrollTop - finalScrollTop) <= 1) {
              clickIndex.current = null;
            }
          }

          if (scrollTarget) {
            scrollTarget.scrollTo({
              top: finalScrollTop,
              behavior: "smooth",
            });
          }
        }
      }
    },
    [
      scrollToSelectedTabContent,
      tabContent,
      scrollToSelectedTabContentOffsetTop,
      scrollToSelected,
      scrollToSelectedTabParent,
    ],
  );

  // 탭 포커스 계산
  const tabIndexCalc = useCallback(
    (tabContent: HTMLElement) => {
      const tabContents = tabContent.children;
      let index = null;
      for (let i = 0; i < tabContents.length; i++) {
        const tabContent = tabContents[i] as HTMLElement;
        const rect = tabContent.getBoundingClientRect();
        const maxRect = -tabContent.clientHeight + scrollToSelectedTabContentOffsetTop - 1;
        const minRect = scrollToSelectedTabContentOffsetTop + 1;
        if (rect.top <= minRect && rect.top >= maxRect) {
          index = i;
        }
      }
      if (index === null) {
        // 스크롤이 탭 컨텐츠 범위 밖으로 나갔을 때 가장 가까운 탭 포커스
        let min = Infinity;
        for (let i = 0; i < tabContents.length; i++) {
          const tabContent = tabContents[i] as HTMLElement;
          const rect = tabContent.getBoundingClientRect();
          const maxRect = -tabContent.clientHeight + scrollToSelectedTabContentOffsetTop - 1;
          const minRect = scrollToSelectedTabContentOffsetTop + 1;
          const distance = Math.min(Math.abs(rect.top - minRect), Math.abs(rect.top - maxRect));
          if (distance < min) {
            min = distance;
            index = i;
          }
        }
      }
      return index;
    },
    [scrollToSelectedTabContentOffsetTop],
  );

  // 터치 이벤트 핸들러(탭 클릭 후 이동 완료되었을때 clickIndex 초기화 위함)
  const touchEventHandler = useCallback((e: TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target && tabRef.current?.contains(target)) {
      // 탭 영역 클릭 했을때는 제외
    } else {
      // 탭 이외 영역 클릭 시에는 초기화
      clickIndex.current = null;
    }
  }, []);
  // 휠 이벤트 핸들러(탭 클릭 후 이동 완료되었을때 clickIndex 초기화 위함)
  const wheelEventHandler = useCallback((e: WheelEvent) => {
    clickIndex.current = null;
  }, []);

  // 탭 컨텐츠 옵저버
  const observerRef = useRef<IntersectionObserver[]>([]);
  const intersectionObserverHandler = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (!tabContent) return;
      if (clickIndex.current !== null) return;
      const index = tabIndexCalc(tabContent);
      if (index !== null && tabs && tabs?.length > 0) {
        const newIndex = Math.min(Math.max(0, index), tabs.length - 1);
        setFocusedIndex(newIndex);
      }
    },
    [tabContent],
  );

  // 탭컨텐츠 있을때 옵저버 등록 (스크롤 되면 탭 활성화되도록)
  useEffect(() => {
    if (!tabContent) return;
    const sections = Array.from(tabContent?.children || []);
    sections?.forEach((section, index) => {
      observerRef.current[index] = new IntersectionObserver(intersectionObserverHandler, {
        threshold: Array.from({ length: 5 }, (_, i) => i * 0.25),
        rootMargin: `-${scrollToSelectedTabContentOffsetTop}px 0px -${scrollToSelectedTabContentOffsetBottom}px 0px`,
      });
      observerRef.current[index].observe(section);
    });
    window.addEventListener("touchstart", touchEventHandler);
    window.addEventListener("wheel", wheelEventHandler);

    return () => {
      observerRef?.current?.forEach((observer) => {
        observer.disconnect();
      });
      window.removeEventListener("touchstart", touchEventHandler);
      window.removeEventListener("wheel", wheelEventHandler);
    };
  }, [tabContent]);

  return (
    <div
      ref={tabRef}
      className={`${styles.tab} ${type ? styles["tab-" + type] : ""} ${detailType ? styles["tab-" + detailType] : ""} ${className ? className : ""}`}
      style={{
        margin: marginForCustom ? marginForCustom : "",
      }}
    >
      <ul>
        {tabs.map((tab, index) => (
          <li
            key={"tab" + tab.label}
            className={index === focusedIndex ? styles["focused"] : ""}
            onClick={() => {
              tab?.onClick && tab.onClick();
              handleTabClick(index);
            }}
          >
            {type === "image" && (
              <div className={styles["image"]}>
                {tab?.url && <img src={tab.url} alt={tab.label} />}
              </div>
            )}
            <span>
              <i label-text={tab.label}></i>
              <span className="only-sr">{tab.label}</span>
              {tab.count !== undefined && (
                <span
                  className={styles["count"]}
                  style={{
                    color: countColor ? countColor : "",
                  }}
                >
                  {tab.count}
                </span>
              )}
              {tab?.badge &&
                tab.badge.map((badge, badgeIndex) =>
                  badge?.isShow ? (
                    <Badge
                      key={"tab" + tab.label + "-badge" + badge.type + badgeIndex}
                      type={badge.type}
                      text={badge?.text}
                      badgePosition="relative"
                      marginForCustom={
                        type === "text" || type === "image" ? "0 0 0 2px" : "0 0 0 10px"
                      }
                      backgroundColor={
                        index === focusedIndex
                          ? badge?.selectedbackgroundColor
                          : badge?.backgroundColor
                      }
                    />
                  ) : null,
                )}
              {tab?.component && tab.component}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Tab;
